import { createFileRoute } from "@tanstack/react-router";
import { PortalLayout } from "@/components/portal-layout";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Clock, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/admin/my-assignments")({
  component: MyAssignmentsPage,
});

function MyAssignmentsPage() {
  const { user } = useAuth();

  const assignmentsQ = useQuery({
    queryKey: ["admin-teacher-assignments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: myClasses } = await supabase
        .from("classes")
        .select("id, name")
        .eq("teacher_id", user!.id);

      if (!myClasses?.length) return [];

      const classIds = myClasses.map((c) => c.id);
      const classMap = Object.fromEntries(myClasses.map((c) => [c.id, c]));

      const { data: assignments } = await supabase
        .from("assignments")
        .select("id, title, description, due_at, class_id, created_at")
        .in("class_id", classIds)
        .order("created_at", { ascending: false });

      const assignmentIds = (assignments ?? []).map((a) => a.id);
      const { data: submissions } = await supabase
        .from("submissions")
        .select("assignment_id")
        .in("assignment_id", assignmentIds.length > 0 ? assignmentIds : ["00000000-0000-0000-0000-000000000000"]);

      const subCount = new Map<string, number>();
      (submissions ?? []).forEach((s) => {
        subCount.set(s.assignment_id, (subCount.get(s.assignment_id) ?? 0) + 1);
      });

      return (assignments ?? []).map((a) => ({
        ...a,
        className: classMap[a.class_id]?.name ?? "",
        submissionCount: subCount.get(a.id) ?? 0,
        isPastDue: a.due_at ? new Date(a.due_at) < new Date() : false,
      }));
    },
  });

  const assignments = assignmentsQ.data ?? [];

  return (
    <PortalLayout
      portal="admin"
      title="Bài tập"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Bài tập" }]}
    >
      {assignments.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="size-10 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          <h3 className="font-semibold text-lg">Chưa có bài tập</h3>
          <p className="text-sm text-muted-foreground mt-1">Giao bài tập từ trang Lớp học.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map((a) => (
            <div key={a.id} className="p-4 bg-card border rounded-xl flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.className}</div>
              </div>
              {a.due_at && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <Clock className="size-3" strokeWidth={1.5} />
                  {new Date(a.due_at).toLocaleDateString("vi-VN")}
                  {a.isPastDue && (
                    <Badge variant="destructive" className="ml-1 text-[9px]">
                      Quá hạn
                    </Badge>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <Users className="size-3" strokeWidth={1.5} />
                {a.submissionCount} nộp
              </div>
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
